import React, { useState, useRef, useEffect } from 'react';
import { useJurisdictionSearch } from '@/hooks/useJurisdictionSearch';
import { Place } from '@/contexts/PlaceNamesContext';
import { formatPlaceName } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';

export interface HeroSectionProps {
  selectedPlace: Place | null;
  onPlaceSelect: (place: Place) => void;
  onClear?: () => void;
  displayName: string;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  selectedPlace,
  onPlaceSelect,
  onClear,
  displayName
}) => {
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { toast } = useToast();

  // Index of the currently highlighted option for keyboard navigation
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  // Keep highlighted option in view when navigating with arrow keys
  useEffect(() => {
    if (highlightedIndex === -1) return;
    const optionEl = dropdownRef.current?.querySelector<HTMLButtonElement>(`[data-index='${highlightedIndex}']`);
    optionEl?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex]);

  const featuredBenchmarks = [
    { text: 'Newly Permitted Units', icon: 'design_services' },
    { text: 'Existing Housing Stock', icon: 'home_work' },
    { text: 'Housing Stock Growth', icon: 'bar_chart_4_bars' },
    { text: 'Housing Availability & Affordability', icon: 'house' },
    { text: 'Rental Availability & Affordability', icon: 'apartment' },
  ];

  const {
    searchInputValue,
    setSearchInputValue,
    placeOptions,
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  } = useJurisdictionSearch(showAutocomplete);

  // Add logging to track state changes
  useEffect(() => {
    console.log('State update:', {
      searchInputValue,
      isEditing,
      selectedPlace: selectedPlace?.name,
      displayName
    });
  }, [searchInputValue, isEditing, selectedPlace, displayName]);

  // Update effect to handle selectedPlace changes
  useEffect(() => {
    console.log('Selected place changed:', {
      selectedPlace: selectedPlace?.name,
      displayName
    });
    
    if (selectedPlace && displayName) {
      setSearchInputValue(displayName);
      setIsEditing(false);
      setHasSearched(false);
    } else {
      setSearchInputValue('');
      setIsEditing(false);
      setHasSearched(false);
    }
  }, [selectedPlace, displayName]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Input changing:', value);
    setIsEditing(true);
    setSearchInputValue(value);
    setShowAutocomplete(true);
    setHasSearched(false);

    // Reset highlighted option when user types
    setHighlightedIndex(-1);
  };

  const handleInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault(); // Prevent default paste behavior
    const pastedText = e.clipboardData.getData('text');
    console.log('Content pasted:', pastedText);
    
    // Set the pasted value and trigger search
    setSearchInputValue(pastedText);
    setIsEditing(true);
    setShowAutocomplete(true);
    setHasSearched(false);
    
    // Reset highlighted option
    setHighlightedIndex(-1);
  };

  const handleInputFocus = () => {
    console.log('Input focused');
    setShowAutocomplete(true);

    // Whenever the dropdown is reopened, default to the first item
    if (placeOptions.length > 0) {
      setHighlightedIndex(0);
    }
  };

  const handleInputBlur = () => {
    console.log('Input blurred');
    setTimeout(() => {
      setIsEditing(false);
      setShowAutocomplete(false);
      clearResults();
      setHasSearched(false);
    }, 150);
  };

  const handleSelectItem = (place: Place & { displayName: string }) => {
    console.log('Selecting new place - FULL OBJECT:', place);
    console.log('Selecting new place:', {
      newPlace: place.name,
      displayName: place.displayName,
      place_fips: place.place_fips
    });
    
    // Reset search state
    setSearchInputValue('');
    setIsEditing(false);
    setShowAutocomplete(false);
    setHasSearched(false);
    clearResults();
    
    // Call parent handler to update selectedPlace and displayName
    onPlaceSelect(place);
    
    // Blur input and scroll
    inputRef.current?.blur();
    
    // Mobile-specific feedback
    if (isMobile) {
      toast({
        title: "Data Loaded!",
        description: `${formatPlaceName(place.name)} benchmark data is ready below.`,
        action: (
          <button
            onClick={() => {
              window.scrollBy({
                top: window.innerHeight,
                behavior: 'smooth'
              });
            }}
            className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            View Results
          </button>
        ),
      });
    }
    
    // Scroll down one viewport height after selecting a city
    setTimeout(() => {
      window.scrollBy({
        top: window.innerHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  // Handle keyboard events (arrow navigation + select)
  const handleClear = () => {
    console.log('Clearing selected place');
    setSearchInputValue('');
    setIsEditing(false);
    setShowAutocomplete(false);
    setHasSearched(false);
    clearResults();
    setHighlightedIndex(-1);
    
    if (onClear) {
      onClear();
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // If there are search results, select the highlighted one
      if (showAutocomplete && placeOptions.length > 0 && highlightedIndex >= 0) {
        handleSelectItem(placeOptions[highlightedIndex]);
        return;
      }
      
      // If there's only one result, select it
      if (showAutocomplete && placeOptions.length === 1) {
        handleSelectItem(placeOptions[0]);
        return;
      }
      
      // If there are multiple results but none highlighted, select the first
      if (showAutocomplete && placeOptions.length > 0 && highlightedIndex === -1) {
        handleSelectItem(placeOptions[0]);
        return;
      }
      
      // If no results but we have search input, mark as searched to show "no results"
      if (searchInputValue.trim() && placeOptions.length === 0 && !isSearching && !isDebouncing) {
        setHasSearched(true);
        setShowAutocomplete(true);
      }
      
      return;
    }

    if (!showAutocomplete || placeOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev + 1;
          return next >= placeOptions.length ? 0 : next;
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev - 1;
          return next < 0 ? placeOptions.length - 1 : next;
        });
        break;
      }
      case 'Escape': {
        setShowAutocomplete(false);
        setHasSearched(false);
        break;
      }
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
          inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowAutocomplete(false);
        setHasSearched(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Track when search completes to show "no results" if needed
  useEffect(() => {
    if (!isSearching && !isDebouncing && searchInputValue.trim() && showAutocomplete && placeOptions.length === 0) {
      // Only show "no results" if the current search doesn't match the selected place
      const currentSearchLower = searchInputValue.toLowerCase().trim();
      const selectedPlaceLower = selectedPlace?.name.toLowerCase().trim();
      const displayNameLower = displayName.toLowerCase().trim();
      
      const isSearchingForSelectedPlace = selectedPlaceLower === currentSearchLower || 
                                        displayNameLower === currentSearchLower ||
                                        (selectedPlace && formatPlaceName(selectedPlace.name).toLowerCase().trim() === currentSearchLower);
      
      if (!isSearchingForSelectedPlace) {
        setHasSearched(true);
      }
    }
  }, [isSearching, isDebouncing, searchInputValue, showAutocomplete, placeOptions.length, selectedPlace, displayName]);

  return (
    <section className="w-full py-16 pb-12 sm:pb-16 md:pb-20 bg-gradient-to-br from-[#CA0079] to-[#F1AB11] mt-0">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 2xl:px-32">
        <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-[1.3] font-geist">
          Permit Benchmarking
        </h1>
        <div className="mt-6 sm:mt-8 md:mt-[30px]">
          <p className="text-white text-xl sm:text-2xl md:text-[32px] font-semibold leading-[1.3] sm:leading-[42px] font-geist">
              Cities grow. Permitting and housing need to keep up. See how your jurisdiction measures up.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full gap-6 lg:gap-8 xl:gap-12 mt-8 sm:mt-12 md:mt-[60px]">
          <div className="lg:col-start-1">
            <h2 className="text-white font-geist-mono text-base font-semibold tracking-[0.32px] uppercase leading-[130%]">
              Featured Benchmarks
            </h2>
            <ul className="flex w-full flex-col text-lg sm:text-xl text-white font-normal mt-5 space-y-4 font-geist">
              {featuredBenchmarks.map((benchmark, index) => (
                <li
                  key={index}
                  className="flex items-center gap-3"
                >
                  <span className="material-symbols-outlined w-6 h-6 text-2xl flex-shrink-0">
                    {benchmark.icon}
                  </span>
                  <span>{benchmark.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-start-2">
            <h2 className="text-white font-geist-mono text-base font-semibold tracking-[0.32px] uppercase leading-[130%]">
              get started here
            </h2>
            <div className="w-full text-lg sm:text-xl text-[#CA0079] font-normal mt-5 relative font-geist">
              <label htmlFor="jurisdiction-search" className="sr-only">
                Select your jurisdiction
              </label>
              <div className="relative">
                <input
                  ref={inputRef}
                  id="jurisdiction-search"
                  type="text"
                  value={isEditing ? searchInputValue : displayName}
                  onChange={handleInputChange}
                  onPaste={handleInputPaste}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  onKeyDown={handleInputKeyDown}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  aria-autocomplete="list"
                  className={`text-[#CA0079] bg-white w-full py-3 sm:py-4 rounded-[11px] focus:outline-none focus:ring-2 focus:ring-[#CA0079] text-base sm:text-lg font-geist ${selectedPlace && !isEditing ? 'pl-12 sm:pl-14 pr-20 sm:pr-24' : 'pl-12 sm:pl-14'}`}
                />
                
                {/* Persistent magnifying glass icon */}
                <div className="absolute left-4 sm:left-6 top-1/2 transform -translate-y-1/2 pointer-events-none flex items-center h-full">
                  <span className="material-symbols-outlined text-[#CA0079] text-xl opacity-50 leading-none">
                    search
                  </span>
                </div>

                {/* Clear button - only show when a place is selected and not editing */}
                {selectedPlace && !isEditing && (
                  <button
                    onClick={handleClear}
                    className="absolute right-4 sm:right-6 top-1/2 transform -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-md bg-gray-200 hover:bg-gray-300 transition-colors"
                    aria-label="Clear selected jurisdiction"
                    type="button"
                  >
                    <span className="material-symbols-outlined text-[#CA0079] text-sm leading-none">
                      close
                    </span>
                    <span className="text-[#CA0079] text-sm font-medium">
                      Clear
                    </span>
                  </button>
                )}

                {/* Placeholder text */}
                {!searchInputValue && !displayName && (
                  <div className="absolute left-12 sm:left-14 top-1/2 transform -translate-y-1/2 pointer-events-none">
                    <span className="text-[#CA0079] text-base sm:text-lg uppercase font-medium opacity-50 font-geist">
                      Select your jurisdiction
                    </span>
                  </div>
                )}
              </div>

              {/* Autocomplete dropdown */}
              {showAutocomplete && placeOptions.length > 0 && (
                <div 
                  ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 max-h-60 overflow-y-auto"
                >
                  {placeOptions.map((place, index) => (
                    <button
                      key={place.place_fips}
                      data-index={index}
                      onClick={() => handleSelectItem(place)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      className={`w-full text-left px-4 sm:px-6 py-3 transition-colors text-base sm:text-lg first:rounded-t-[11px] last:rounded-b-[11px] font-geist ${index === highlightedIndex ? 'bg-[#CA0079] text-white' : 'text-[#CA0079] hover:bg-gray-50'}`}
                      role="option"
                      aria-selected={index === highlightedIndex}
                    >
                      {place.displayName}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Loading state */}
              {isDebouncing && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 px-4 sm:px-6 py-3 sm:py-4 text-center text-[#CA0079] text-base sm:text-lg font-geist">
                  Searching...
                </div>
              )}
              
              {/* No results found state */}
              {hasSearched && !isSearching && !isDebouncing && placeOptions.length === 0 && searchInputValue.trim() && (() => {
                // Only show "no results" if the current search doesn't match the selected place
                const currentSearchLower = searchInputValue.toLowerCase().trim();
                const selectedPlaceLower = selectedPlace?.name.toLowerCase().trim();
                const displayNameLower = displayName.toLowerCase().trim();
                
                const isSearchingForSelectedPlace = selectedPlaceLower === currentSearchLower || 
                                                  displayNameLower === currentSearchLower ||
                                                  (selectedPlace && formatPlaceName(selectedPlace.name).toLowerCase().trim() === currentSearchLower);
                
                return !isSearchingForSelectedPlace;
              })() && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-gray-200 z-50 px-4 sm:px-6 py-3 sm:py-4 text-center text-[#CA0079] text-base sm:text-lg font-geist">
                  No jurisdictions found
                </div>
              )}
              
              {/* Error state */}
              {searchError && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-[11px] shadow-lg border border-red-200 z-50 px-4 sm:px-6 py-3 sm:py-4 text-center text-red-500 text-base sm:text-lg font-geist">
                  {searchError}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
